<template>
  <!-- eslint-disable max-len -->
  <div>
    <main class="instant-savings-desktop u-container u-no-print">
      <h1 class="font-bold">
        Official USPS<sup>®</sup> Change-of-Address
      </h1>
      <IndividualProgressBarWithSteps
        v-if="showSimplifyExperience"
        :mobile="false"
      />
      <IndividualProgressBar
        v-if="!showSimplifyExperience"
        role="progressbar"
        :aria-valuenow="progressStep"
        :aria-valuemin="current"
        :aria-valuemax="1"
      />
      <div class="mt-8">
        <h2 ref="stepHeading">
          <template v-if="!showChoiceExperience && !isDesktop">
            Step 4:&nbsp;
          </template>
          <span class="font-bold">Instant savings</span>
        </h2>
        <p class="mt-4 text-blue-200 font-bold">
          Get these exclusive savings as a thank you for completing your Change-of-Address.
        </p>
        <p class="w-2/3 mt-4">
          Before confirming your information, select offers from your favorite brands by checking
          the boxes below. Your offers will automatically be emailed to the email address you
          provided. If you would like to print the coupons or have them sent to your phone via text
          message, click "Continue" below.
        </p>
      </div>
      <form
        ref="form"
        novalidate
        @submit.prevent="submit"
      >
        <button
          id="continue-top-btn"
          ref="continueTop"
          class="c-btn mt-6"
          @click="
            analytics.click.continue();
            deliveryMethod = '';
            textOffersSent = false;
          "
        >
          Continue
        </button>
        <div
          v-if="offers && offers.length"
          class="flex flex-wrap mt-8"
        >
          <component
            :is="getAmpComponent(template.schema.name)"
            v-for="({ accountId, accountName, adGroupId, id, template }, objectId) in offers"
            ref="offerTile"
            :key="id"
            :value="selectedOffers.includes(offers[objectId])"
            :account-id="accountId"
            :account-name="accountName"
            :ad-id="id"
            :ad-group-id="adGroupId"
            :alt-text="accountName"
            :background-url="template.properties.backgroundUrl"
            :cta="template.properties.cta"
            :headline="template.properties.headline"
            :legal-text="template.properties.legalText"
            :logo-url="template.properties.logoUrl"
            :short-description="template.properties.shortDescription"
            :tooltip="template.properties.tooltip"
            :template-id="template.id"
            class="instant-savings-desktop__offer-tile"
            @selected:offer="toggleOfferSelect(offers[objectId])"
          />
        </div>
        <p class="w-2/3 mt-8 text-sm">
          Neither the U.S. Postal Service® nor any contractor associated with the operation of this
          website: (1) make any guarantee as to the accuracy of the statements made by the
          advertisers or the content of the editorial material on this website; (2) endorse any
          company, product or service listed or advertised in this MoversGuide Online™ product; or
          (3) will be held liable in any manner for any claim, loss, expense, damage or consumer
          dissatisfaction arising out of or in connection with the use of the MoversGuide Online™
          product.
        </p>
        <div class="c-hairline mt-8" />
        <button
          id="continue-bot-btn"
          ref="continueBot"
          class="c-btn mt-6"
          @click="analytics.click.continue"
        >
          Continue
        </button>
      </form>
    </main>
    <Modal
      v-model="isModalOpen"
      class="u-no-print"
      @input="isModalOpen = false"
    >
      <div class="modal-offers-delivery flex flex-col items-center">
        <p class="text-blue-200 font-bold">
          How would you like to get your offers?
        </p>
        <div class="flex mt-6">
          <div class="flex flex-col mr-6">
            <SvgPrinter
              :active="deliveryMethod === 'Print'"
              class="mb-5 mx-auto"
            />
            <button
              id="print-btn"
              ref="printBtn"
              :class="{ 'c-btn--secondary': deliveryMethod !== 'Print' }"
              class="c-btn"
              @click="
                deliveryMethod = 'Print';
                print();
                registerSelections();
                analytics.offersModal.print();
              "
            >
              Print
            </button>
          </div>
          <div class="flex flex-col">
            <SvgPhone
              :active="deliveryMethod === 'Text'"
              class="mb-5 mx-auto"
            />
            <button
              id="text-btn"
              ref="textBtn"
              :class="{ 'c-btn--secondary': deliveryMethod !== 'Text' }"
              class="c-btn"
              @click="
                deliveryMethod = 'Text';
                registerSelections();
                analytics.offersModal.text();
              "
            >
              Text
            </button>
          </div>
        </div>
        <div
          v-if="deliveryMethod === 'Print'"
          class="mt-6 text-center"
        >
          <p>
            Your offers should print now.
          </p>
          <p class="mt-2">
            If you need to, you can
            <button
              id="print-again-btn"
              ref="modalPrintAgainBtn"
              class="c-link c-link--inline"
              @click="print"
            >
              print them again.
            </button>
          </p>
        </div>
        <div
          v-if="deliveryMethod === 'Text'"
          class="flex flex-col items-center mt-6"
        >
          <template v-if="!textOffersSent">
            <p class="w-2/3 text-center text-sm">
              By providing my mobile phone number below, I agree to receive a promotional text
              message from USPS® using an automated system. I understand that my consent to receive
              the text message is not required to buy goods or services. Carrier charges may apply.
            </p>
            <InputPhone
              ref="phoneInput"
              v-model="phoneNumber"
              label="Mobile Phone Number"
              placeholder="(000) 000-0000"
              name="phone"
              :has-error="errors.phoneNumber.hasError"
              class="w-1/2 mt-6"
              @blur="analytics.offersModal.phoneNumberField"
            >
              <template #helper>
                {{ errors.phoneNumber.message }}
              </template>
            </InputPhone>
          </template>
          <template v-else>
            <p
              ref="modalTextSuccess"
              class="w-2/3 text-center text-sm"
            >
              Thank you. Your offers will be delivered by text message using an automated system to
              <strong>{{ phoneNumber }}</strong>.
            </p>
          </template>
        </div>
        <div class="flex justify-center mt-6">
          <button
            class="c-btn c-btn--secondary"
            @click="
              isModalOpen = false;
              analytics.offersModal.selectMoreOffers();
            "
          >
            Select More Offers
          </button>
          <button
            v-if="deliveryMethod === 'Text' && !textOffersSent"
            id="send-text-btn"
            ref="sendTextBtn"
            class="c-btn ml-6"
            @click="
              validate();
              analytics.offersModal.sendOffers();
            "
          >
            Send Offers
          </button>
          <button
            v-else
            id="modal-continue-btn"
            ref="modalContinueBtn"
            class="c-btn ml-6"
            @click="
              submit();
              analytics.offersModal.continue();
            "
          >
            Continue
          </button>
        </div>
      </div>
    </Modal>
    <div
      v-if="printableOffers && printableOffers.length"
      class="u-print-only"
    >
      <component
        :is="getAmpComponent(template.schema.name)"
        v-for="{ accountId, adGroupId, id, template } in printableOffers"
        :key="id"
        :account-id="accountId"
        :ad-id="id"
        :ad-group-id="adGroupId"
        :template-id="template.id"
        :background-url="template.properties.backgroundUrl"
        :background-url-two="template.properties.backgroundUrlTwo"
        :background-url-three="template.properties.backgroundUrlThree"
        :barcode="template.properties.barcode"
        :borderless="template.properties.borderless"
        :expiration="template.properties.expiration"
        :headline-url="template.properties.headlineUrl"
        :legal-text="template.properties.legalText"
        :logo-url="template.properties.logoUrl"
        :promo-code="template.properties.promoCode"
        :promo-code-two="template.properties.promoCodeTwo"
        :promo-code-three="template.properties.promoCodeThree"
        :promo-code-label="template.properties.promoCodeLabel"
        :promo-code-label-two="template.properties.promoCodeLabelTwo"
        :promo-code-label-three="template.properties.promoCodeLabelThree"
        :tagline="template.properties.tagline"
        @print="print"
      />
    </div>
  </div>
</template>

<script>
import {
  AdvantageDouble,
  AdvantageSingle,
  AdvantagePrintDefault,
  AdvantagePrintComcast,
} from '@ampersend/mgo';
import {
  InputPhone,
  Modal,
  IndividualProgressBarWithSteps,
  IndividualProgressBar,
} from '@mgo/ui/lib/components';
import Cookies from 'js-cookie';
import { isDesktop } from '../../utils/deviceType';
import { desktopAnalytics } from './analytics';
import SvgPhone from '../../components/SvgPhone.vue';
import SvgPrinter from '../../components/SvgPrinter.vue';
import InstantSavingsMixin from './mixin';

export default {
  name: 'DesktopInstantSavings',
  components: {
    AdvantageDouble,
    AdvantageSingle,
    AdvantagePrintDefault,
    AdvantagePrintComcast,
    InputPhone,
    Modal,
    IndividualProgressBarWithSteps,
    SvgPhone,
    SvgPrinter,
    IndividualProgressBar,
  },
  mixins: [InstantSavingsMixin],
  data() {
    return {
      deliveryMethod: '',
      outData: [],
    };
  },
  computed: {
    isDesktop,
    showChoiceExperience: () => JSON.parse(sessionStorage.getItem('showChoiceExperience')),
    analytics() {
      return desktopAnalytics();
    },
    printableOffers() {
      return this.getPrintableOffers(this.rawOffers);
    },
    schemaNames() {
      return ['MGO Advantage Double', 'MGO Advantage Single'];
    },
  },
  watch: {
    isModalOpen(current) {
      if (current) {
        this.analytics.offersModal.opened();
      } else {
        this.analytics.offersModal.closed();
      }
    },
  },
  async mounted() {
    if (this.cfpsId !== '') {
      this.$coa.reset();
      this.$deleteSession();
      Cookies.remove('coa');
      Cookies.remove('coa_complete');
      localStorage.removeItem('ampersend_data');
    }
  },
  methods: {
    isPrintTemplateAvailable(offer) {
      return offer.templates.find(
        ({ schema }) => ['MGO Advantage Print Default', 'MGO Advantage Print Comcast'].indexOf(schema.name) > -1,
      );
    },
    /**
     * Print offers by template
     * @param {Array} offers
     * @return {Array}
     */
    getPrintableOffers(offers) {
      return offers
        .filter(this.isPrintTemplateAvailable)
        .filter(({ id }) => this.selectedOffers.map(({ id: i }) => i).indexOf(id) > -1)
        .map((offer) => {
          const { templates, id, ...rest } = offer;
          const schemaNames = ['MGO Advantage Print Default', 'MGO Advantage Print Comcast'];
          const [template] = templates.filter((t) => schemaNames.indexOf(t.schema.name) > -1);

          return {
            adId: id,
            id,
            templateId: template.id,
            template,
            ...rest,
          };
        });
    },
    async print() {
      await this.$nextTick();
      window.print();
      this.printedOffers = true;
    },
    async textOffers() {
      const payload = {
        phone: this.phoneNumber.replace(/-/g, ''),
        message: await this.getAmpersendExperienceText() || null,
      };
      await this.$store.dispatch(
        'instantSavings/getTextOffers',
        payload,
      );
      this.textOffersSent = true;
    },
  },
};
</script>
